import React from 'react'
import type { AnomalyType } from '../types'

interface AnomalyBadgeProps {
  anomalyType: AnomalyType
  statType: string
  zScore?: number | null
  isSpike?: boolean
  compact?: boolean
}

const ANOMALY_CONFIG: Record<AnomalyType, { label: string; emoji: string }> = {
  stat_spike: { label: 'Spike', emoji: 'trending_up' },
  stat_drop: { label: 'Drop', emoji: 'trending_down' },
  sequence_break: { label: 'Streak Ended', emoji: 'BRKS' },
  variance_outlier: { label: 'Outlier', emoji: '!' },
}

function formatStatType(stat: string): string {
  return stat
    .replace(/_/g, ' ')
    .replace(/per 90/i, '/90')
    .replace(/average/i, 'avg')
}

export function AnomalyBadge({
  anomalyType,
  statType,
  zScore,
  isSpike,
  compact = false,
}: AnomalyBadgeProps) {
  const config = ANOMALY_CONFIG[anomalyType]

  if (!config) {
    return null
  }

  // Determine if this is an upward or downward anomaly
  const isUp = anomalyType === 'stat_spike' || isSpike === true
  const color = isUp ? 'var(--green)' : 'var(--red)'
  const arrow = isUp ? '+' : '-'

  if (compact) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.125rem',
          padding: '0.125rem 0.25rem',
          background: `color-mix(in srgb, ${color} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
          borderRadius: '3px',
          fontSize: '0.5625rem',
          fontWeight: 600,
          color,
        }}
        title={`${formatStatType(statType)} ${zScore ? `(${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}z)` : ''}`}
      >
        {arrow}
        {formatStatType(statType)}
      </span>
    )
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.25rem 0.5rem',
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
        borderRadius: '4px',
        fontSize: '0.6875rem',
      }}
    >
      <span
        style={{
          fontWeight: 700,
          color,
          fontSize: '0.75rem',
        }}
      >
        {arrow}
      </span>
      <span style={{ color: 'var(--fg)', fontWeight: 500 }}>
        {formatStatType(statType)}
      </span>
      {zScore && (
        <span
          style={{
            fontSize: '0.5625rem',
            color: 'var(--muted)',
            fontWeight: 500,
          }}
        >
          {Math.abs(zScore).toFixed(1)}z
        </span>
      )}
    </div>
  )
}

// Helper to render multiple anomaly badges
interface AnomalyBadgesProps {
  anomalies: Array<{
    anomaly_type: AnomalyType
    stat_type: string
    z_score?: number | null
  }>
  limit?: number
  compact?: boolean
}

export function AnomalyBadges({ anomalies, limit = 3, compact = true }: AnomalyBadgesProps) {
  const visible = anomalies.slice(0, limit)
  const remaining = anomalies.length - limit

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {visible.map((a, i) => (
        <AnomalyBadge
          key={i}
          anomalyType={a.anomaly_type}
          statType={a.stat_type}
          zScore={a.z_score}
          isSpike={a.anomaly_type === 'stat_spike'}
          compact={compact}
        />
      ))}
      {remaining > 0 && (
        <span
          style={{
            fontSize: '0.5625rem',
            color: 'var(--muted)',
            padding: '0.125rem',
          }}
        >
          +{remaining}
        </span>
      )}
    </div>
  )
}

export default AnomalyBadge
